import React, { useEffect, useRef, useState } from 'react';
import { AppState, StatusBar, Animated, Text, StyleSheet, View, TouchableOpacity, ActivityIndicator } from 'react-native';
import * as Updates from 'expo-updates';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { PaperProvider } from 'react-native-paper';
import { NavigationContainer, DarkTheme, DefaultTheme, LinkingOptions } from '@react-navigation/native';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import * as Linking from 'expo-linking';
import { initI18n } from './src/i18n';
import RootNavigator from './src/navigation/RootNavigator';
import type { RootStackParamList } from './src/navigation/types';
import { darkTheme, lightTheme, darkColors, lightColors } from './src/theme';
import { useThemeStore } from './src/stores/themeStore';
import { useAuthStore } from './src/stores/authStore';
import { usersApi } from './src/api/users';
import { paymentsApi } from './src/api/payments';
import { checkLegalStatus } from './src/utils/checkLegalStatus';
import LoadingIndicator from './src/components/common/LoadingIndicator';
import ActivationSuccessBanner from './src/components/common/ActivationSuccessBanner';
import MembershipPausedBanner from './src/components/common/MembershipPausedBanner';

const JUST_UPDATED_KEY = 'cbjj_client_just_updated';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const navDarkTheme = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    background: darkColors.bg,
    card: darkColors.bgHeader,
    text: darkColors.text,
    border: darkColors.border,
    primary: darkColors.accent,
  },
};

const navLightTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: lightColors.bg,
    card: lightColors.bgHeader,
    text: lightColors.text,
    border: lightColors.border,
    primary: lightColors.accent,
  },
};

const linking: LinkingOptions<any> = {
  prefixes: [Linking.createURL('/'), 'cbjj://'],
  config: {
    screens: {
      Auth: {
        path: '',
        screens: {
          AuthCallback: 'auth/callback',
          ResetPassword: 'reset-password',
          EmailVerification: 'verify-email',
          SubUserRegister: 'invite/:token',
        },
      },
      AccountDeletion: 'account-deletion',
      PublicLegalDoc: 'PublicLegalDoc',
    },
  },
  getInitialURL: async () => {
    // Check if initial URL has auth verifier - handle root path callback
    const initialUrl = await Linking.getInitialURL();
    if (initialUrl) {
      const url = new URL(initialUrl);
      if (url.searchParams.has('neon_auth_session_verifier')) {
        // Has verifier but not on auth/callback path - rewrite to auth/callback
        if (!url.pathname.includes('auth/callback')) {
          url.pathname = '/auth/callback';
          return url.toString();
        }
      }
    }
    return initialUrl;
  },
  subscribe: (listener) => {
    const subscription = Linking.addEventListener('url', ({ url }) => {
      // Intercept URLs with session verifier not on auth/callback path
      try {
        const parsed = new URL(url);
        if (parsed.searchParams.has('neon_auth_session_verifier')) {
          if (!parsed.pathname.includes('auth/callback')) {
            parsed.pathname = '/auth/callback';
            listener(parsed.toString());
            return;
          }
        }
      } catch {
        // Invalid URL, pass through as-is
      }
      listener(url);
    });
    return () => subscription.remove();
  },
};

export default function App() {
  const themeMode = useThemeStore((s) => s.mode);
  const isDark = themeMode === 'dark';
  const [i18nReady, setI18nReady] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [deepLinkRoute, setDeepLinkRoute] = useState<keyof RootStackParamList | undefined>(undefined);
  const [deepLinkChecked, setDeepLinkChecked] = useState(false);
  const hydrated = useAuthStore((s) => s.hydrated);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [updateToastVisible, setUpdateToastVisible] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [applyingUpdate, setApplyingUpdate] = useState(false);
  const toastOpacity = useRef(new Animated.Value(0)).current;
  const checkingUpdateRef = useRef(false);
  const updateReadyRef = useRef(false);       // ref mirror — AppState listener holds a stale closure
  const lastUpdateCheckRef = useRef(0);

  // Fetch a pending OTA update (if any) and surface the "Update now" banner.
  // Applying is user-initiated — Updates.reloadAsync() only runs on the tap,
  // from a settled app state, never automatically mid-lifecycle. Reads only
  // refs/setters so a stale closure (the AppState listener) still behaves.
  const checkAndFetchUpdate = async () => {
    if (__DEV__ || checkingUpdateRef.current || updateReadyRef.current) return;
    // Throttle foreground re-checks so app-switching doesn't hammer the network
    const now = Date.now();
    if (now - lastUpdateCheckRef.current < 60_000) return;
    lastUpdateCheckRef.current = now;
    checkingUpdateRef.current = true;
    try {
      const update = await Updates.checkForUpdateAsync();
      if (update.isAvailable) {
        await Updates.fetchUpdateAsync();
        // Surface the banner FIRST — a failed cache write must not hide a
        // successfully-downloaded update.
        updateReadyRef.current = true;
        setUpdateReady(true);
        try { await AsyncStorage.setItem(JUST_UPDATED_KEY, '1'); } catch { /* best effort */ }
      }
    } catch (_) {
      // Silently ignore update errors
    } finally {
      checkingUpdateRef.current = false;
    }
  };

  const applyUpdate = async () => {
    setApplyingUpdate(true);
    try {
      await Updates.reloadAsync();
    } catch (e) {
      // If the reload can't complete, leave the banner up; the update still
      // applies on the next cold start.
      console.warn('[OTA] reloadAsync failed; update will apply on next launch', e);
      setApplyingUpdate(false);
    }
  };

  useEffect(() => {
    // Never leave the app stuck on the loading screen if init throws.
    initI18n().then(() => setI18nReady(true)).catch(() => setI18nReady(true));
  }, []);

  // Detect deep link to account-deletion before rendering navigator
  useEffect(() => {
    Linking.getInitialURL().then((url) => {
      if (url && url.includes('account-deletion')) {
        setDeepLinkRoute('AccountDeletion');
      }
      setDeepLinkChecked(true);
    }).catch(() => setDeepLinkChecked(true));
  }, []);

  // Check for OTA updates on startup (foreground re-checks are wired below)
  useEffect(() => {
    checkAndFetchUpdate();
  }, []);

  // Show toast after update reload
  useEffect(() => {
    (async () => {
      const flag = await AsyncStorage.getItem(JUST_UPDATED_KEY);
      if (flag === '1') {
        await AsyncStorage.removeItem(JUST_UPDATED_KEY);
        setUpdateToastVisible(true);
        Animated.sequence([
          Animated.timing(toastOpacity, { toValue: 1, duration: 300, useNativeDriver: true }),
          Animated.delay(3000),
          Animated.timing(toastOpacity, { toValue: 0, duration: 400, useNativeDriver: true }),
        ]).start(() => setUpdateToastVisible(false));
      }
    })();
  }, []);

  useEffect(() => {
    if (!i18nReady || !hydrated) return;
    const state = useAuthStore.getState();
    if (!state.isAuthenticated) {
      setAuthReady(true);
      return;
    }
    // Validate existing session before showing any authenticated screens
    usersApi.getMe()
      .then(async () => {
        try {
          // Fetch membership status and pending verification on startup
          const [membershipCheck, pending] = await Promise.all([
            paymentsApi.getActiveMembership(),
            paymentsApi.getPendingVerification(),
          ]);

          const store = useAuthStore.getState();
          const hasActive = membershipCheck.has_active_membership;

          // Check if main user's membership is paused
          const mainUserMembership = membershipCheck.members?.find(m => m.user_id === store.user?.id);
          const isMainUserPaused = mainUserMembership?.membership?.status === 'paused';

          // User needs subscription only if they have no active/paused membership
          store.setNeedsSubscription(!hasActive && !isMainUserPaused);

          // Clear paused state by default
          store.setHasPausedMembership(false, null);

          if (isMainUserPaused && mainUserMembership) {
            // Main user has paused membership - allow app access with banner
            store.setHasPausedMembership(true, mainUserMembership);
            // Clear any pending verification (paused takes priority)
            store.setPendingVerificationPaymentId(null);
            store.setActiveVietQRPayment(null);
          } else if (!hasActive) {
            if (pending.payment_id && pending.user_marked_paid_at) {
              // They don't have active membership, but have a customer-confirmed (marked paid) pending payment
              store.setPendingVerificationPaymentId(pending.payment_id);
              store.setActiveVietQRPayment({
                payment_id: pending.payment_id,
                order_number: pending.order_number || '',
                amount: pending.amount ? parseFloat(pending.amount) : 0,
                qr_url: pending.qr_url || '',
                bank_bin: pending.bank_bin || '',
                account_number: pending.account_number || '',
                account_name: pending.account_name || '',
                user_marked_paid_at: pending.user_marked_paid_at,
                order_items: pending.order_items ? pending.order_items.map(item => ({
                  label: item.label,
                  description: item.description,
                  subtotal: item.subtotal,
                  discount_amount: item.discount_amount,
                  final_amount: item.final_amount
                })) : [],
              });
            } else {
              // No pending payment, or it exists but has not been confirmed/marked paid by user yet
              store.setPendingVerificationPaymentId(null);
              store.setActiveVietQRPayment(null);
            }
          } else {
            // User has active membership: clear any pending verification references
            store.setPendingVerificationPaymentId(null);
            store.setActiveVietQRPayment(null);
          }

          // Check legal agreements status
          await checkLegalStatus();
        } catch (err) {
          console.error('Failed to verify subscription on startup:', err);
          // If checking fails, let it fallback safely to store-persisted values
        } finally {
          setAuthReady(true);
        }
      })
      .catch(() => {
        setAuthReady(true);
      });
  }, [i18nReady, hydrated]);

  // Re-check legal status on app resume
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'active') {
        // Catch updates published while the app sat in the background
        checkAndFetchUpdate();
        const store = useAuthStore.getState();
        if (store.isAuthenticated) {
          if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
          resumeTimerRef.current = setTimeout(() => {
            checkLegalStatus();
          }, 500);
        }
      }
    });
    return () => {
      subscription.remove();
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, []);

  if (!i18nReady || !authReady || !deepLinkChecked) {
    return (
      <SafeAreaProvider>
        <PaperProvider theme={isDark ? darkTheme : lightTheme}>
          <LoadingIndicator />
        </PaperProvider>
      </SafeAreaProvider>
    );
  }

  return (
    <SafeAreaProvider>
      <QueryClientProvider client={queryClient}>
        <PaperProvider theme={isDark ? darkTheme : lightTheme}>
          <NavigationContainer theme={isDark ? navDarkTheme : navLightTheme} linking={linking}>
            <StatusBar
              barStyle={isDark ? 'light-content' : 'dark-content'}
              backgroundColor={isDark ? darkColors.bg : lightColors.bg}
            />
            <ActivationSuccessBanner />
            <MembershipPausedBanner />
            <RootNavigator initialRouteName={deepLinkRoute} />
            {updateReady && (
              <SafeAreaView edges={['bottom']} style={styles.updateBanner} pointerEvents="box-none">
                <View style={styles.updateBannerInner}>
                  <Text style={styles.updateBannerText}>A new version is ready.</Text>
                  <TouchableOpacity
                    style={styles.updateBannerBtn}
                    onPress={applyUpdate}
                    disabled={applyingUpdate}
                    activeOpacity={0.8}
                  >
                    {applyingUpdate ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <Text style={styles.updateBannerBtnText}>Update now</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </SafeAreaView>
            )}
            {updateToastVisible && (
              <Animated.View style={[styles.updateToast, { opacity: toastOpacity }]}>
                <Text style={styles.updateToastText}>App updated successfully</Text>
              </Animated.View>
            )}
          </NavigationContainer>
        </PaperProvider>
      </QueryClientProvider>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  updateToast: {
    position: 'absolute',
    bottom: 100,
    alignSelf: 'center',
    backgroundColor: '#1e1e1e',
    borderWidth: 1,
    borderColor: '#e63329',
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  updateBanner: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  updateBannerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: 12,
    marginBottom: 8,
    paddingLeft: 16,
    paddingRight: 8,
    paddingVertical: 8,
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e63329',
  },
  updateBannerText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  updateBannerBtn: {
    backgroundColor: '#e63329',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    minWidth: 96,
    alignItems: 'center',
  },
  updateBannerBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  updateToastText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
});
